import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/apiService'

const UserCertificates = () => {
  const navigate = useNavigate()
  const [sessions, setSessions] = useState([])
  const [selectedSession, setSelectedSession] = useState('')
  const [tournaments, setTournaments] = useState([])
  const [selectedTournament, setSelectedTournament] = useState('')
  const [selectedGender, setSelectedGender] = useState('')
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [loadingTournaments, setLoadingTournaments] = useState(false)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    loadSessions()
  }, [])

  useEffect(() => {
    if (selectedSession) loadTournaments()
  }, [selectedSession])

  const loadSessions = async () => {
    setLoadingSessions(true)
    try {
      const response = await api.get('/user/certificates/sessions')
      if (response.data.success) {
        const data = response.data.data || []
        setSessions(data)
        if (data.length > 0) {
          setSelectedSession(data[0])
        }
      }
    } catch (error) {
      console.error('Error loading sessions:', error)
    } finally {
      setLoadingSessions(false)
    }
  }

  const loadTournaments = async () => {
    setLoadingTournaments(true)
    setSelectedTournament('')
    setSelectedGender('')
    try {
      const response = await api.get(`/user/certificates/published?session=${selectedSession}`)
      if (response.data.success) {
        setTournaments(response.data.data || [])
      }
    } catch (error) {
      console.error('Error loading tournaments:', error)
    } finally {
      setLoadingTournaments(false)
    }
  }

  const handleTournamentChange = (value) => {
    setSelectedTournament(value)
    setSelectedGender('')
  }

  const handleDownload = async () => {
    if (!selectedTournament || !selectedGender) {
      showNotification('Please select all options', 'error')
      return
    }

    setDownloading(true)
    try {
      const response = await api.post('/user/certificates/download-my-team', {
        event_id: parseInt(selectedTournament),
        gender: selectedGender
      }, {
        responseType: 'blob',
        headers: { 'Accept': 'application/pdf' }
      })

      if (response.data.type === 'application/json') {
        const text = await response.data.text()
        const json = JSON.parse(text)
        showNotification(json.message || 'Error generating certificates', 'error')
        return
      }

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const downloadUrl = window.URL.createObjectURL(blob)
      const genderLabel = selectedGender === 'M' ? 'Male' : 'Female'

      const pdfWindow = window.open(downloadUrl, '_blank')
      if (!pdfWindow) {
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `${genderLabel}_Certificates.pdf`
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }

      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 5000)
      showNotification('Certificate PDF opened in new tab', 'success')
    } catch (error) {
      console.error('Error downloading certificates:', error)
      const msg = error.response?.status === 403
        ? 'Certificates are not published for this event'
        : error.response?.status === 404
          ? `No ${selectedGender === 'M' ? 'male' : 'female'} results found for your team`
          : 'Error generating certificates'
      showNotification(msg, 'error')
    } finally {
      setDownloading(false)
    }
  }

  const showNotification = (message, type) => {
    const notification = document.createElement('div')
    notification.style.cssText = `
      position: fixed; top: 20px; right: 20px; padding: 1rem 1.5rem; border-radius: 8px;
      color: white; font-weight: 600; z-index: 10000; display: flex; align-items: center; gap: 10px;
      background: ${type === 'success' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)'};
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `
    notification.innerHTML = `<i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}"></i> ${message}`
    document.body.appendChild(notification)
    setTimeout(() => notification.remove(), 3000)
  }

  const selectStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '0.95rem',
    background: 'white'
  }

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#374151',
    fontSize: '0.9rem'
  }

  return (
    <div style={{ padding: '1.5rem 2rem', width: '100%', minHeight: '100vh', background: '#f3f4f6' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 50%, #c00f15 100%)',
        padding: '1.5rem 2rem',
        color: 'white',
        borderRadius: '12px',
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem', fontWeight: '700', color: '#ffffff' }}>
            <i className="fas fa-certificate" style={{ marginRight: '12px' }}></i>
            My Certificates
          </h1>
          <p style={{ margin: 0, fontSize: '1rem', color: '#ffffff' }}>
            Download certificates for tournaments you participated in
          </p>
        </div>
        <button
          onClick={() => navigate('/user/dashboard')}
          style={{
            background: 'white',
            border: 'none',
            color: '#c00f15',
            borderRadius: '8px',
            padding: '10px 20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '0.85rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Select Options */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="fas fa-filter" style={{ color: '#c00f15' }}></i>
          Select Options
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
          {/* Session */}
          <div>
            <label style={labelStyle}>Session *</label>
            <select
              value={selectedSession}
              onChange={e => setSelectedSession(e.target.value)}
              style={selectStyle}
              disabled={loadingSessions}
            >
              <option value="">Select Session</option>
              {sessions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Tournament */}
          <div>
            <label style={labelStyle}>Tournament *</label>
            <select
              value={selectedTournament}
              onChange={e => handleTournamentChange(e.target.value)}
              style={{ ...selectStyle, opacity: !selectedSession ? 0.6 : 1 }}
              disabled={!selectedSession || loadingTournaments}
            >
              <option value="">Select Tournament</option>
              {tournaments.map(t => (
                <option key={t.id} value={t.id}>{t.event_name}</option>
              ))}
            </select>
            {selectedSession && tournaments.length === 0 && !loadingTournaments && (
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#ef4444' }}>
                No published tournaments for this session
              </p>
            )}
          </div>

          {/* Gender */}
          <div>
            <label style={labelStyle}>Gender *</label>
            <select
              value={selectedGender}
              onChange={e => setSelectedGender(e.target.value)}
              style={{ ...selectStyle, opacity: !selectedTournament ? 0.6 : 1 }}
              disabled={!selectedTournament}
            >
              <option value="">Select Gender</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
          </div>

          {/* Download Button */}
          <div>
            <button
              onClick={handleDownload}
              disabled={downloading || !selectedGender}
              style={{
                width: '100%',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '8px',
                cursor: (downloading || !selectedGender) ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '0.95rem',
                color: 'white',
                background: 'linear-gradient(135deg, #c00f15, #a00d12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: (downloading || !selectedGender) ? 0.6 : 1
              }}
            >
              {downloading ? (
                <><i className="fas fa-spinner fa-spin"></i> Generating...</>
              ) : (
                <><i className="fas fa-download"></i> Download Certificate</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Info card */}
      {!selectedTournament && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '3rem', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <i className="fas fa-trophy" style={{ fontSize: '4rem', color: '#d1d5db', marginBottom: '1rem', display: 'block' }}></i>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>Select a Tournament to Start</h3>
          <p style={{ margin: 0, color: '#6b7280' }}>Choose a session and tournament to download your certificates.</p>
        </div>
      )}
    </div>
  )
}

export default UserCertificates
